/**
 * Public clinic-facing Referral Program portal (/referrals).
 *
 * Views:
 *   1. landing  — program intro + registration form (criteria, client,
 *                 clinic info, consent) → POST /referrals/register
 *   2. success  — the two codes (reusable clinic code + per-client code)
 *                 + print registration sheet (PDF) + jump to status
 *   3. lookup   — enter a code: client code → status view (under review /
 *                 approved weekly reporting); clinic code → clinic info
 *                 editor + its clients + "register another"
 *
 * No auth — the code IS the auth (same model as /patient). All amounts and
 * the week count are server-side.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREEN = "#0F3B2E";
const LABEL = "#2D5A4C";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const BG = "#F4F3EF";
const RED = "#B4351B";
const NAVY = "#1F4E79";
const TEAL = "#0D6E63";
const SUCCESS = "#2E7D5B";
const AMBER = "#E58A2E";

const CRITERIA = [
  "Newly diagnosed — confirmed bloodwork required and no history of prior treatment available",
  "Previously had Hepatitis C — no blood work in 12 months and sharing pipes, stems, straws, etc.",
  "Needles (IV drugs) — shared needles, syringes, spoons, or washes, etc.",
  "Documented bloodwork confirming elevated liver enzymes",
  "Spent time in jail — 1 in 10 people have been exposed (fights, shared razors, etc.)",
];

// ── helpers ────────────────────────────────────────────────────────────
function normalizeCode(v) {
  return (v || "").replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 8);
}
function displayCode(v) {
  const r = normalizeCode(v);
  return r.length === 8 ? `${r.slice(0, 4)}-${r.slice(4)}` : r;
}

// iOS PDF bounce recovery: opening the registration sheet PDF replaces the
// tab on mobile Safari; Back reloads the app at "/" (App.js returns us to
// /referrals via pp_last_path) — this saved view restores the codes/status
// page the user was on. sessionStorage survives the in-tab bounce.
const REF_VIEW_KEY = "ref_view_state";
const REF_VIEW_TTL_MS = 30 * 60 * 1000; // 30 min
function readSavedView() {
  try {
    const raw = sessionStorage.getItem(REF_VIEW_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || Date.now() - (data.ts || 0) > REF_VIEW_TTL_MS) return null;
    return data;
  } catch { return null; }
}
function fmtPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length > 0) return `(${d}`;
  return "";
}
function fmtHealthCard(v) {
  // Ontario health card: exactly 10 digits first (numbers only), then an
  // optional 2-letter version code. Anything else is refused as you type;
  // a space is auto-inserted before the version code for readability.
  const raw = (v || "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
  let digits = "", letters = "";
  for (const ch of raw) {
    if (/[0-9]/.test(ch) && letters === "" && digits.length < 10) digits += ch;
    else if (/[A-Z]/.test(ch) && digits.length === 10 && letters.length < 2) letters += ch;
  }
  return digits + (letters ? " " + letters : "");
}

// Stored clinic address is one joined string with historical variants
// ("street, ON A1A 1A1", "street, city ON A1A1A1", "street city, ON").
// Extract the postal code wherever it sits, drop the province token, and
// keep any city with the street so no part of the address is ever lost.
function splitAddress(addr) {
  const s = (addr || "").trim();
  if (!s) return { street: "", postal: "" };
  const comma = s.indexOf(",");
  let street = comma >= 0 ? s.slice(0, comma).trim() : s;
  let rest = comma >= 0 ? s.slice(comma + 1).trim() : "";
  const pm = rest.match(/([A-Z]\d[A-Z]\s?\d[A-Z]\d)/i);
  const postal = pm ? pm[1].toUpperCase().replace(/^([A-Z]\d[A-Z])\s?(\d[A-Z]\d)$/, "$1 $2") : "";
  if (pm) rest = rest.replace(pm[0], "");
  rest = rest.replace(/\b[A-Z]{2}\b/g, "").replace(/\s{2,}/g, " ").trim();  // province token
  if (rest) street = street ? `${street}, ${rest}` : rest;
  return { street, postal };
}
// "street, city ON A1A 1A1" → ["street", "city ON A1A 1A1"] for two-line display
function addrLines(addr) {
  const s = (addr || "").trim();
  const i = s.indexOf(",");
  if (i < 0) return [s, ""];
  return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
}
function fmtDateLong(v) {  if (!v) return "—";
  const d = new Date(String(v).slice(0, 10) + "T12:00:00");
  if (isNaN(d)) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}
const money = (n) => `$${Math.round(n || 0)}`;

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return fallback;
}

// ── shared atoms ───────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh", background: BG, color: "#1C2B26",
    fontFamily: "'Work Sans', system-ui, sans-serif", paddingTop: 72,
  },
  hero: { background: GREEN, color: "#fff", padding: "22px 20px 26px" },
  heroInner: { maxWidth: 640, margin: "0 auto", textAlign: "center" },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 800, letterSpacing: "-.01em" },
  heroSub: { fontSize: 12, opacity: .78, marginTop: 2, letterSpacing: ".02em" },
  container: { maxWidth: 740, margin: "0 auto", padding: "0 20px 96px" },
  card: {
    background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14,
    padding: 20, marginTop: 20,
  },
  secband: {
    background: "rgba(15,59,46,.06)", border: `1px solid ${BORDER}`,
    borderRadius: 10, padding: "9px 13px", fontSize: 12, fontWeight: 800,
    letterSpacing: ".1em", textTransform: "uppercase", color: LABEL,
    marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8,
  },
  lbl: { display: "block", fontSize: 11, fontWeight: 700, color: LABEL, letterSpacing: ".04em", margin: "10px 0 4px", paddingLeft: 13 },
  inp: {
    width: "100%", height: 40, border: `1px solid ${BORDER}`, borderRadius: 8,
    padding: "0 12px", fontSize: 14, fontFamily: "inherit", background: "#fff",
    outline: "none",
  },
  btn: {
    width: "100%", height: 44, border: 0, borderRadius: 10, marginTop: 16,
    background: GREEN, color: "#fff", fontSize: 14, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
  },
  refcode: { fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontWeight: 700, letterSpacing: ".06em" },
};

function SecBand({ children, right }) {
  return (
    <div style={S.secband}>
      <span>{children}</span>
      {right ? <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".02em", textTransform: "none", color: MUTED }}>{right}</span> : null}
    </div>
  );
}

function Field({ label, required, children, half }) {
  return (
    <div style={half ? { flex: "0 0 calc(50% - 5px)", minWidth: 0 } : undefined}>
      <label style={S.lbl}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: "flex", gap: 10 }}>{children}</div>;
}

// ── main component ─────────────────────────────────────────────────────
export default function Referrals() {
  const [view, setView] = useState(() => {
    const s = readSavedView();
    return s && (s.view === "success" || s.view === "lookup") ? s.view : "landing";
  }); // landing | success | lookup
  const [busy, setBusy] = useState(false);

  // missing-field highlighting — set on submit, cleared as fields are filled
  const [missing, setMissing] = useState(new Set());
  const clearMiss = (k) => setMissing((m) => {
    if (!m.has(k)) return m;
    const n = new Set(m);
    n.delete(k);
    return n;
  });
  // fax already in the system — show the existing record, confirm before duplicating
  const [faxConflict, setFaxConflict] = useState(null); // {physician_name, code_display, address, phone, fax_display, retry, offices?, msg?}
  // full input props: red border + scroll marker while the field is flagged
  // (border shorthand in BOTH states — mixing shorthand with borderColor/
  // borderWidth longhands makes React drop the frame when the red clears)
  const inp = (k) => ({
    style: missing.has(k) ? { ...S.inp, border: `1.5px solid ${RED}` } : S.inp,
    ...(missing.has(k) ? { "data-invalid": "1" } : {}),
  });

  // registration form
  const EMPTY = {
    first_name: "", last_name: "", dob: "", health_card: "",
    physician_name: "", clinic_name: "", cpso: "",
    street: "", province: "ON", postal: "", phone: "", fax: "", email: "",
  };
  const [form, setForm] = useState(EMPTY);
  const [criteria, setCriteria] = useState([false, false, false, false, false]);
  const [consent, setConsent] = useState(false);
  const [critError, setCritError] = useState(false);
  const [result, setResult] = useState(() => {
    const s = readSavedView();
    return s && s.view === "success" ? (s.result || null) : null;
  });

  // lookup
  const [code, setCode] = useState(() => {
    const s = readSavedView();
    return s && s.view === "lookup" ? (s.code || "") : "";
  });
  const [lookupType, setLookupType] = useState(null); // client | clinic
  const [record, setRecord] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [clinicClients, setClinicClients] = useState([]);
  const [clinicForm, setClinicForm] = useState(null);
  const [weekBusy, setWeekBusy] = useState(false);

  // multi-office — clinic account editor
  const [offices, setOffices] = useState([]);
  const [payAll, setPayAll] = useState(true);
  const [officeEdit, setOfficeEdit] = useState(null); // office id | "new" | null
  const [oForm, setOForm] = useState(null);           // {id?, name, street, postal, phone, fax, email}
  const [officeFilter, setOfficeFilter] = useState(""); // "" | "__all" | office id

  // register-from-account (clinic code entered first)
  const [clinicCtx, setClinicCtx] = useState(null);   // { code, code_display, offices, physician_name, cpso, email }
  const [siteSel, setSiteSel] = useState("");         // "" | office id | "__other"
  const [saveOffice, setSaveOffice] = useState(true);

  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === "phone" || k === "fax") v = fmtPhone(v);
    if (k === "health_card") v = fmtHealthCard(v);
    setForm((f) => ({ ...f, [k]: v }));
    clearMiss(k);
  };

  const toggleCrit = (i) => {
    setCriteria((c) => c.map((x, j) => (j === i ? !x : x)));
    setCritError(false);
  };

  // View swaps (form → success / lookup) replace a tall page with a short
  // one — jump straight back to the top or the user lands at the footer.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [view]);

  // keep the phone/browser Back button inside the page flow: success and
  // lookup are pushed as history entries, so Back returns to the form
  // instead of leaving /referrals entirely
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => {
    const onPop = () => {
      if (viewRef.current === "success" || viewRef.current === "lookup") {
        setView("landing");
        setLookupType(null);
        setClinicCtx(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function navView(v) {
    if (v === "success" || v === "lookup") window.history.pushState({ refView: v }, "");
    setView(v);
  }

  async function submitRegistration(e, allowDup = false) {
    if (e && e.preventDefault) e.preventDefault();
    setFaxConflict(null);
    // flag every empty required field at once — red borders beat one-by-one
    // native popups
    const miss = new Set();
    for (const k of ["dob", "first_name", "last_name", "health_card"]) {
      if (!String(form[k] || "").trim()) miss.add(k);
    }
    if (clinicCtx) {
      if (!siteSel) miss.add("site");
      if (siteSel === "__other") {
        for (const k of ["street", "postal", "phone", "fax", "email"]) {
          if (!String(form[k] || "").trim()) miss.add(k);
        }
      }
    } else {
      for (const k of ["physician_name", "cpso", "street", "postal", "phone", "fax", "email"]) {
        if (!String(form[k] || "").trim()) miss.add(k);
      }
    }
    if (miss.size) {
      setMissing(miss);
      toast.error("Please complete the fields highlighted in red.");
      setTimeout(() => document.querySelector('[data-invalid="1"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
      return;
    }
    if (!criteria.some(Boolean)) {
      setCritError(true);
      toast.error("Please select at least 1 criteria to continue.");
      return;
    }
    if (!consent) {
      toast.error("Please confirm the client's verbal consent.");
      return;
    }
    setBusy(true);
    try {
      const selOffice = clinicCtx && siteSel && siteSel !== "__other"
        ? (clinicCtx.offices || []).find((o) => o.id === siteSel)
        : null;
      // an office added moments ago but not yet saved — send it as a new
      // site to persist instead of an unknown office_id
      const selUnsaved = selOffice && String(selOffice.id).startsWith("tmp-");
      const address = selOffice
        ? selOffice.address
        : `${form.street.trim()}, ${form.province.trim()} ${form.postal.trim()}`.replace(/^,|,$/g, "");
      const payload = {
        first_name: form.first_name, last_name: form.last_name, dob: form.dob,
        health_card: form.health_card, criteria, consent,
        physician_name: clinicCtx ? clinicCtx.physician_name : form.physician_name,
        clinic_name: selOffice ? selOffice.name : form.clinic_name,
        cpso: clinicCtx ? clinicCtx.cpso : form.cpso,
        address,
        phone: selOffice ? selOffice.phone : form.phone,
        fax: selOffice ? selOffice.fax : form.fax,
        email: selOffice ? (selOffice.email || clinicCtx.email) : form.email,
      };
      if (clinicCtx) {
        payload.clinic_code = clinicCtx.code_display || clinicCtx.code;
        if (selOffice && !selUnsaved) payload.office_id = selOffice.id;
        else if (selUnsaved) payload.save_office = true;
        else payload.save_office = saveOffice;
      }
      if (allowDup) payload.allow_duplicate_fax = true;
      const { data } = await axios.post(`${API}/referrals/register`, payload);
      setResult(data);
      setClinicCtx(null);
      navView("success");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && typeof d === "object" && d.error === "fax_exists") {
        setFaxConflict({ ...d.existing, retry: "register" });
        setTimeout(() => document.querySelector('[data-testid="fax-conflict-box"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
      } else {
        toast.error(errText(err, "Couldn't submit — please check the form and try again."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function doLookup(rawCode) {
    const c = displayCode(rawCode || code);
    if (normalizeCode(c).length !== 8) {
      toast.error("Enter the full 8-character code (XXXX-XXXX).");
      return;
    }
    setBusy(true);
    setFaxConflict(null);
    try {
      const { data } = await axios.get(`${API}/referrals/lookup/${c}`);
      if (data.type === "client") {
        setLookupType("client");
        setRecord(data.record);
        setCode(data.record.code_display);
      } else {
        setLookupType("clinic");
        setClinic(data.clinic);
        setClinicClients(data.clients || []);
        setClinicForm({
          physician_name: data.clinic.physician_name || "",
          cpso: data.clinic.cpso || "",
          email: data.clinic.email || "",
        });
        setPayAll(data.clinic.pay_all_email !== false);
        setOffices(data.clinic.offices || []);
        setOfficeFilter("");
        setOfficeEdit(null);
        setOForm(null);
        setCode(data.clinic.code_display);
      }
      navView("lookup");  // top-of-form quick box jumps straight to results
    } catch (err) {
      toast.error(errText(err, "No clinic or client found for that code."));
    } finally {
      setBusy(false);
    }
  }

  // Persist the codes/status view so an iOS PDF bounce (Back from the
  // viewer reloads the app) can restore it; cleared on landing.
  useEffect(() => {
    try {
      if (view === "success" && result) {
        sessionStorage.setItem(REF_VIEW_KEY, JSON.stringify({ view, result, ts: Date.now() }));
      } else if (view === "lookup" && normalizeCode(code).length === 8) {
        sessionStorage.setItem(REF_VIEW_KEY, JSON.stringify({ view, code, ts: Date.now() }));
      } else if (view === "landing") {
        sessionStorage.removeItem(REF_VIEW_KEY);
      }
    } catch { /* ignore */ }
  }, [view, result, code]);

  // Rehydrate after a bounce: lookup re-fetches its record (and pushes its
  // own history entry); success just needs the Back-to-form history entry.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    const s = readSavedView();
    if (!s) return;
    if (s.view === "lookup" && s.code) {
      doLookup(s.code);
    } else if (s.view === "success" && s.result) {
      window.history.pushState({ refView: "success" }, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function persistClinic(nextOffices, successMsg, allowDup = false) {
    setBusy(true);
    setFaxConflict(null);
    try {
      const { data } = await axios.put(`${API}/referrals/clinic/${clinic.code_display}`, {
        physician_name: clinicForm.physician_name,
        cpso: clinicForm.cpso,
        email: clinicForm.email,
        pay_all_email: payAll,
        offices: nextOffices.map((o) => ({
          id: String(o.id).startsWith("tmp-") ? undefined : o.id,
          name: o.name, address: o.address, phone: o.phone, fax: o.fax, email: o.email,
        })),
        ...(allowDup ? { allow_duplicate_fax: true } : {}),
      });
      setClinic(data);
      setOffices(data.offices || []);
      setPayAll(data.pay_all_email !== false);
      if (successMsg) toast.success(successMsg);
      return data;
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && typeof d === "object" && d.error === "fax_exists") {
        setFaxConflict({ ...d.existing, retry: "clinic", offices: nextOffices, msg: successMsg });
        setTimeout(() => document.querySelector('[data-testid="fax-conflict-box"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
      } else {
        toast.error(errText(err, "Couldn't update the clinic info."));
      }
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function saveClinic() {
    if (officeEdit) {
      toast.error("Save or cancel the office you're editing first.");
      return null;
    }
    return persistClinic(offices, "Clinic information updated — this record is now the most current.");
  }

  // ── office sites: Save office / Remove persist to the account right away,
  // so nothing depends on remembering the main save button ──
  function openOfficeEdit(o) {
    const a = splitAddress(o.address);
    setOfficeEdit(o.id);
    setOForm({
      id: o.id, name: o.name, street: a.street, postal: a.postal,
      phone: o.phone_display || fmtPhone(o.phone),
      fax: o.fax_display || fmtPhone(o.fax),
      email: o.email || "",
    });
  }
  function openOfficeAdd() {
    setOfficeEdit("new");
    setOForm({ id: null, name: "", street: "", postal: "", phone: "", fax: "", email: clinicForm?.email || "" });
  }
  async function saveOfficeLocal() {
    const f = oForm;
    const miss = new Set();
    if (!f.name.trim()) miss.add("o_name");
    if (!f.street.trim()) miss.add("o_street");
    if (!f.postal.trim()) miss.add("o_postal");
    if (!f.phone.trim()) miss.add("o_phone");
    if (!f.fax.trim()) miss.add("o_fax");
    if (!f.email.trim()) miss.add("o_email");
    if (miss.size) {
      setMissing((m) => new Set([...m, ...miss]));
      toast.error("Fill in every office field — name, address, phone, fax and email.");
      setTimeout(() => document.querySelector('[data-invalid="1"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
      return;
    }
    const rec = {
      id: f.id || `tmp-${Date.now()}`,
      name: f.name.trim(),
      address: `${f.street.trim()}, ON ${f.postal.trim()}`,
      phone: f.phone, fax: f.fax, email: f.email.trim(),
    };
    const next = f.id ? offices.map((o) => (o.id === f.id ? rec : o)) : [...offices, rec];
    setOffices(next);
    setOfficeEdit(null);
    setOForm(null);
    await persistClinic(next, "Office saved to your account.");
  }
  async function removeOfficeLocal(id) {
    if (offices.length <= 1) {
      toast.error("Keep at least one office on the account.");
      return;
    }
    if (!window.confirm("Remove this office from your account? Clients registered from it keep their records.")) return;
    const next = offices.filter((o) => o.id !== id);
    setOffices(next);
    setOfficeEdit(null);
    setOForm(null);
    await persistClinic(next, "Office removed from your account.");
  }

  async function submitWeek(n) {
    setWeekBusy(true);
    try {
      await axios.post(`${API}/referrals/clients/${record.code_display}/weeks`, { week: n });
      toast.success(`Week ${n} report submitted — $75 to the clinic`);
      await doLookup(record.code_display);
    } catch (err) {
      toast.error(errText(err, "Couldn't submit the week."));
    } finally {
      setWeekBusy(false);
    }
  }

  const cf = (k) => (e) => {
    let v = e.target.value;
    if (k === "phone" || k === "fax") v = fmtPhone(v);
    setClinicForm((f) => ({ ...f, [k]: v }));
  };

  const filteredClients = !officeFilter || officeFilter === "__all"
    ? clinicClients
    : clinicClients.filter((c) => (c.office_id || offices[0]?.id) === officeFilter);
  // payment-email choice only matters once money is actually moving
  const hasQualified = clinicClients.some((c) => c.status === "approved");

  // confirm "add anyway" after seeing the record that already owns the fax
  async function confirmFaxConflict() {
    const fc = faxConflict;
    if (!fc) return;
    setFaxConflict(null);
    if (fc.retry === "register") await submitRegistration(null, true);
    else if (fc.retry === "clinic") await persistClinic(fc.offices, fc.msg, true);
  }

  const faxConflictBox = faxConflict && (
    <div style={{ border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px", background: "rgba(180,53,27,.04)", margin: "0 0 14px", lineHeight: 1.5 }} data-testid="fax-conflict-box">
      <b style={{ fontSize: 12.5, color: RED, display: "block", marginBottom: 6 }}>⚠ This fax number is already in our system</b>
      <div style={{ fontSize: 12.5, color: "#1C2B26" }}>
        Registered to: <b>{faxConflict.physician_name}</b>
        <br />Clinic code: <span style={{ ...S.refcode, color: NAVY }} data-testid="fax-conflict-code">{faxConflict.code_display}</span>
        {faxConflict.address ? (<>
          <br />{addrLines(faxConflict.address)[0]}
          {addrLines(faxConflict.address)[1] ? <><br />{addrLines(faxConflict.address)[1]}</> : null}
        </>) : null}
        {faxConflict.phone ? <><br />Phone: {faxConflict.phone}</> : null}
        {faxConflict.fax_display ? <><br />Fax: {faxConflict.fax_display}</> : null}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button type="button" style={{ ...S.btn, marginTop: 0, height: 38, flex: 1, fontSize: 13, background: RED }} onClick={confirmFaxConflict} disabled={busy} data-testid="fax-conflict-confirm">
          {busy ? "Saving…" : "Add as new record"}
        </button>
        <button type="button" style={{ ...S.btn, marginTop: 0, height: 38, flex: 1, fontSize: 13, background: "#fff", color: LABEL, border: `1px solid ${BORDER}` }} onClick={() => setFaxConflict(null)} data-testid="fax-conflict-cancel">Cancel</button>
      </div>
    </div>
  );

  // selected registration site (account flow) — with display fallbacks so
  // freshly added offices show a fax number even before server formatting
  const selSite = clinicCtx && siteSel && siteSel !== "__other"
    ? (clinicCtx.offices || []).find((o) => o.id === siteSel)
    : null;
  const selSiteFax = selSite ? (selSite.fax_display || fmtPhone(selSite.fax)) : "";

  // inline add/edit form for an office site (local until "Save clinic information")
  const officeFormBlock = (isNew) => oForm && (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "2px 12px 12px", marginBottom: 8, background: "#FAFAF7" }} data-testid={isNew ? "office-add-form" : `office-edit-form-${oForm.id}`}>
      <Field label="Clinic Name" required><input {...inp("o_name")} value={oForm.name} onChange={(e) => { setOForm({ ...oForm, name: e.target.value }); clearMiss("o_name"); }} placeholder="East End Walk-in" data-testid="office-f-name" /></Field>
      <Field label="Address" required><input {...inp("o_street")} value={oForm.street} onChange={(e) => { setOForm({ ...oForm, street: e.target.value }); clearMiss("o_street"); }} placeholder="123 Main St" data-testid="office-f-street" /></Field>
      <Row>
        <Field label="Province" required half><input style={{ ...S.inp, background: BG }} value="ON" readOnly data-testid="office-f-province" /></Field>
        <Field label="Postal Code" required half><input {...inp("o_postal")} value={oForm.postal} onChange={(e) => { setOForm({ ...oForm, postal: e.target.value }); clearMiss("o_postal"); }} placeholder="M5V 1J1" data-testid="office-f-postal" /></Field>
      </Row>
      <Row>
        <Field label="Telephone" required half><input {...inp("o_phone")} inputMode="tel" value={oForm.phone} onChange={(e) => { setOForm({ ...oForm, phone: fmtPhone(e.target.value) }); clearMiss("o_phone"); }} placeholder="(416) 555-0143" data-testid="office-f-phone" /></Field>
        <Field label="Fax" required half><input {...inp("o_fax")} inputMode="tel" value={oForm.fax} onChange={(e) => { setOForm({ ...oForm, fax: fmtPhone(e.target.value) }); clearMiss("o_fax"); }} placeholder="(416) 555-0192" data-testid="office-f-fax" /></Field>
      </Row>
      <Field label="Email" required><input type="email" {...inp("o_email")} value={oForm.email} onChange={(e) => { setOForm({ ...oForm, email: e.target.value }); clearMiss("o_email"); }} placeholder="billing@riversideclinic.ca" data-testid="office-f-email" /></Field>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button style={{ ...S.btn, marginTop: 0, height: 38, flex: 1, fontSize: 13, opacity: busy ? 0.7 : 1 }} disabled={busy} onClick={saveOfficeLocal} data-testid="office-save-btn">{busy ? "Saving…" : "Save office"}</button>
        <button style={{ ...S.btn, marginTop: 0, height: 38, flex: 1, fontSize: 13, background: "#fff", color: LABEL, border: `1px solid ${BORDER}` }} onClick={() => { setOfficeEdit(null); setOForm(null); }} data-testid="office-cancel-btn">Cancel</button>
        {!isNew && offices.length > 1 && (
          <button style={{ ...S.btn, marginTop: 0, height: 38, flex: "0 0 auto", width: 68, fontSize: 12, background: "#fff", color: RED, border: `1px solid ${BORDER}` }} onClick={() => removeOfficeLocal(oForm.id)} data-testid="office-remove-btn">Remove</button>
        )}
      </div>
    </div>
  );

  const [pdfBusy, setPdfBusy] = useState(false);

  // fetch the PDF in-page (with feedback) instead of opening a blank new tab
  // that sits black while the server renders it
  async function downloadSheet() {
    if (!result || pdfBusy) return;
    setPdfBusy(true);
    try {
      const resp = await axios.get(
        `${API}/referrals/sheet/${normalizeCode(result.client_code)}.pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `HepCURE-Referral-${result.client_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error("Couldn't prepare the PDF — please try again.");
    } finally {
      setPdfBusy(false);
    }
  }

  // ══════════════ SUCCESS ══════════════
  if (view === "success" && result) {
    return (
      <div style={S.page} data-testid="referrals-success">
        <div style={S.hero}><div style={S.heroInner}>
          <h2 style={S.heroTitle}>HepCURE Registration</h2>
        </div></div>
        <div style={S.container}>
          <div style={S.card}>
            <div style={{ textAlign: "center", padding: "6px 0 2px" }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%", background: SUCCESS, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, fontWeight: 900, margin: "0 auto 8px",
              }}>✓</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: GREEN }}>Patient registered</div>
              <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>
                Registration received — under review.<br />We'll fax your clinic an update.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <div style={{ flex: 1, background: BG, borderRadius: 12, padding: "12px 14px" }} data-testid="success-clinic-code">
                <div style={{ fontSize: 9.5, letterSpacing: ".16em", fontWeight: 800, color: NAVY, textTransform: "uppercase" }}>Clinic code</div>
                <div style={{ ...S.refcode, fontSize: 18, marginTop: 3 }}>{result.clinic_code}</div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>
                  {result.clinic_existing ? "Your existing clinic — info updated" : "Reusable — future registrations"}
                </div>
              </div>
              <div style={{ flex: 1, background: GREEN, color: "#fff", borderRadius: 12, padding: "12px 14px" }} data-testid="success-client-code">
                <div style={{ fontSize: 9.5, letterSpacing: ".16em", fontWeight: 800, textTransform: "uppercase", opacity: .85 }}>Client code</div>
                <div style={{ ...S.refcode, fontSize: 18, marginTop: 3 }}>{result.client_code}</div>
                <div style={{ fontSize: 10.5, opacity: .8, marginTop: 2 }}>This client only — quoted on all faxes</div>
              </div>
            </div>

            <button
              style={{ ...S.btn, opacity: pdfBusy ? 0.7 : 1 }}
              data-testid="print-registration-btn"
              disabled={pdfBusy}
              onClick={downloadSheet}
            >
              {pdfBusy ? "Preparing PDF…" : "Print registration form for your records"}
            </button>
            <button
              style={{ ...S.btn, background: "#fff", color: GREEN, border: `1.5px solid ${GREEN}` }}
              data-testid="goto-status-btn"
              onClick={() => { navView("lookup"); setCode(result.client_code); setLookupType(null); setRecord(null); }}
            >
              Check / manage status →
            </button>
            <div style={{ fontSize: 11.5, color: MUTED, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
              Weekly reporting unlocks once the client is qualified for treatment — we'll fax you.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════ LOOKUP / STATUS ══════════════
  if (view === "lookup") {
    return (
      <div style={S.page} data-testid="referrals-lookup">
        <div style={S.hero}><div style={S.heroInner}>
          <h2 style={S.heroTitle}>HepCURE Registration</h2>
        </div></div>
        <div style={S.container}>
          <div style={S.card}>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...S.inp, ...S.refcode, flex: 1, textTransform: "uppercase" }}
                placeholder="XXXX-XXXX" value={code}
                data-testid="lookup-code-input"
                onChange={(e) => setCode(displayCode(e.target.value))}
                onKeyDown={(e) => e.key === "Enter" && doLookup()}
              />
              <button
                style={{ ...S.btn, width: 90, marginTop: 0, height: 40 }}
                onClick={() => doLookup()} disabled={busy}
                data-testid="lookup-find-btn"
              >Find</button>
            </div>

            {/* ── client: under review (new or pending follow-up) ── */}
            {lookupType === "client" && record && (record.status === "under_review" || record.status === "pending") && (
              <div data-testid="status-under-review">
                <div style={{ textAlign: "center", padding: "14px 0 2px" }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%", background: SUCCESS, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20, fontWeight: 900, margin: "0 auto 8px",
                  }}>✓</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: GREEN }}>Registration received</div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{
                      display: "inline-block", padding: "3px 12px", borderRadius: 999,
                      background: "rgba(229,138,46,.12)", color: "#9A6208",
                      fontSize: 11, fontWeight: 700,
                    }}>Under review</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: MUTED, margin: "10px auto 0", maxWidth: 340, lineHeight: 1.5 }}>
                    Our team is reviewing this registration. Once the client is <b style={{ color: "#3E4C46" }}>qualified for treatment</b>, weekly adherence reminders and payment tracking will unlock here — and we'll fax your clinic an update.
                  </div>
                </div>
                <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, marginTop: 14 }}>
                  {[["Patient", `${record.first_name} ${record.last_name}`],
                    ["Date of birth", record.dob],
                    ["Health card", record.health_card_masked],
                    ["Clinic", record.office_name || record.clinic_name],
                    ["Submitted", fmtDateLong(record.created_at)]].map(([k, v], i, arr) => (
                    <div key={k} style={{
                      display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5,
                      padding: "7px 0", borderBottom: i < arr.length - 1 ? `1px solid #F1F0EC` : 0,
                    }}>
                      <span style={{ color: MUTED }}>{k}</span><b style={{ textAlign: "right" }}>{v || "—"}</b>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, textAlign: "center", marginTop: 12 }}>
                  Nothing to do yet<br />— check back after you receive our fax.
                </div>
              </div>
            )}

            {/* ── client: denied — does not qualify ── */}
            {lookupType === "client" && record && record.status === "denied" && (
              <div data-testid="status-denied">
                <div style={{ textAlign: "center", padding: "14px 0 2px" }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%", background: RED, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20, fontWeight: 900, margin: "0 auto 8px",
                  }}>✕</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: RED }}>This client does not qualify</div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{
                      display: "inline-block", padding: "3px 12px", borderRadius: 999,
                      background: "rgba(180,53,27,.10)", color: RED,
                      fontSize: 11, fontWeight: 700,
                    }}>Not eligible for the program</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: MUTED, margin: "10px auto 0", maxWidth: 340, lineHeight: 1.5 }}>
                    After careful review, this client does not meet the program criteria and <b style={{ color: "#3E4C46" }}>does not qualify</b> for HepCURE services at this time. No further action is needed from your clinic.
                  </div>
                  <div style={{ marginTop: 14, border: "1px solid rgba(31,78,121,.25)", background: "rgba(31,78,121,.06)", borderRadius: 12, padding: 14 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY }}>Questions about this decision?</div>
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Call us — we're happy to walk you through it.</div>
                    <a href="tel:18449252873" data-testid="denied-call-btn" style={{
                      display: "block", marginTop: 10, height: 44, lineHeight: "44px",
                      background: NAVY, color: "#fff", borderRadius: 10,
                      fontSize: 15, fontWeight: 800, letterSpacing: ".02em", textDecoration: "none",
                    }}>1-844-925-CURE (2873)</a>
                  </div>
                </div>
                <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, marginTop: 14 }}>
                  {[["Patient", `${record.first_name} ${record.last_name}`],
                    ["Date of birth", record.dob],
                    ["Health card", record.health_card_masked],
                    ["Clinic", record.office_name || record.clinic_name],
                    ["Submitted", fmtDateLong(record.created_at)],
                    ["Reviewed", fmtDateLong(record.denied_at)]].map(([k, v], i, arr) => (
                    <div key={k} style={{
                      display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5,
                      padding: "7px 0", borderBottom: i < arr.length - 1 ? `1px solid #F1F0EC` : 0,
                    }}>
                      <span style={{ color: MUTED }}>{k}</span><b style={{ textAlign: "right" }}>{v || "—"}</b>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, textAlign: "center", marginTop: 12 }}>
                  Weekly reporting and payment tracking are not available for this client.
                </div>
              </div>
            )}

            {/* ── client: approved — weekly reporting ── */}
            {lookupType === "client" && record && record.status === "approved" && (
              <div data-testid="status-approved">
                <div style={{ paddingLeft: 4, marginTop: 14 }}>
                  <div style={{ marginBottom: 2 }}><b style={{ fontSize: 15 }}>{record.first_name} {record.last_name}</b></div>
                  <div style={{ fontSize: 12.5, color: MUTED }}>Date of birth: {record.dob}</div>
                  <div style={{ fontSize: 12.5, color: MUTED }}>Health card: {record.health_card_masked}</div>
                  <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 10 }} data-testid="status-clinic-name">{record.office_name || record.clinic_name}</div>
                </div>
                <div style={{
                  background: "rgba(46,125,91,.08)", border: "1px solid rgba(46,125,91,.35)",
                  borderRadius: 9, padding: "8px 12px", fontSize: 12, fontWeight: 700,
                  color: SUCCESS, marginBottom: 10,
                }} data-testid="qualified-banner">
                  ✓ Qualified for treatment — approved {fmtDateLong(record.approved_at)} · weekly reporting unlocked
                </div>
                <div style={{
                  background: GREEN, color: "#fff", borderRadius: 9, padding: "8px 12px",
                  fontSize: 12, fontWeight: 700, display: "flex", justifyContent: "space-between", marginBottom: 8,
                }} data-testid="paid-bar">
                  <span>Paid to clinic to date</span><span>{money(record.paid_total)} of {money(record.max_total)}</span>
                </div>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 8, paddingLeft: 4 }}>
                  {record.total_weeks}-week program — set at approval
                </div>

                {Array.from({ length: record.total_weeks || 0 }, (_, i) => i + 1).map((n) => {
                  const w = (record.weeks || []).find((x) => x.n === n);
                  const isNext = !w && n === (record.weeks || []).length + 1;
                  return (
                    <div key={n}
                      data-testid={`week-row-${n}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "9px 11px",
                        borderBottom: `1px solid #F1F0EC`,
                        ...(isNext ? { background: "rgba(13,110,99,.07)", border: `1px solid ${TEAL}`, borderRadius: 9 } : {}),
                        ...(!w && !isNext ? { opacity: .55 } : {}),
                      }}>
                      <span style={{ fontWeight: 700, fontSize: 13, flex: "0 0 58px" }}>Week {n}</span>
                      <span style={{ fontSize: 12.5, color: MUTED, flex: "0 0 34px" }}>$75</span>
                      <span style={{ fontSize: 11.5, color: w?.status === "paid" ? MUTED : isNext ? GREEN : MUTED, flex: 1, fontWeight: isNext ? 600 : 400 }}>
                        {w?.status === "paid" ? `Paid ${fmtDateLong(w.payment_date)}`
                          : w?.status === "approved" ? "Approved — awaiting payment"
                          : w ? "Reported — under review"
                          : isNext ? "Completed — ready to report" : "Not reported yet"}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                        background: w?.status === "paid" ? "rgba(46,125,91,.12)" : w?.status === "approved" ? "rgba(74,111,176,.12)" : w ? "rgba(229,138,46,.14)" : isNext ? "rgba(13,110,99,.12)" : "transparent",
                        color: w?.status === "paid" ? SUCCESS : w?.status === "approved" ? "#4A6FB0" : w ? "#9A6208" : TEAL,
                      }}>
                        {w?.status === "paid" ? "Paid" : w?.status === "approved" ? "Approved" : w ? "Pending" : isNext ? "Report" : "—"}
                      </span>
                    </div>
                  );
                })}

                <div style={{
                  background: "#FFFBEA", border: "1px solid #F0E4B8", borderRadius: 10,
                  padding: "10px 13px", marginTop: 12, fontSize: 11.5, color: "#7A6410", lineHeight: 1.5,
                }} data-testid="weekly-disclaimer">
                  <b>⚠ Please read before submitting</b>
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    <li style={{ marginBottom: 5 }}>Only report a week <b>after</b> the compliance reminder has been completed with the client.</li>
                    <li style={{ marginBottom: 5 }}>Payment is made to the clinic by e-transfer. If auto-deposit is <b>not</b> set up, the security answer / password is: <b>hepcure</b></li>
                    <li>Requests submitted for payment by <b>Friday</b> will be paid the following <b>Monday by 12 PM EST</b>.</li>
                  </ul>
                </div>

                {(record.weeks || []).length < (record.total_weeks || 0) ? (
                  <button
                    style={S.btn}
                    disabled={weekBusy}
                    data-testid="week-submit-btn"
                    onClick={() => submitWeek((record.weeks || []).length + 1)}
                  >
                    {weekBusy ? "Submitting…" : `Submit Week ${(record.weeks || []).length + 1} report — $75 to the clinic`}
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: SUCCESS, fontWeight: 700, textAlign: "center", marginTop: 14 }} data-testid="program-complete">
                    ✓ All {record.total_weeks} weeks reported — program complete
                  </div>
                )}
                <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 10 }}>
                  One week per report. Once approved and paid, the payment date appears beside the week above.
                </div>
              </div>
            )}

            {/* ── clinic: manage info ── */}
            {lookupType === "clinic" && clinic && clinicForm && (
              <div data-testid="clinic-manage">
                <div style={{ display: "flex", gap: 8, margin: "14px 0 10px" }}>
                  <button
                    style={{ ...S.btn, marginTop: 0, height: 40, flex: 1, background: "#fff", color: LABEL, border: `1px solid ${BORDER}`, opacity: busy ? 0.7 : 1 }}
                    data-testid="clinic-register-another"
                    disabled={busy}
                    onClick={async () => {
                      // register from this account: blank patient fields, the
                      // clinic section becomes a site picker fed by the account
                      let offs = offices;
                      if (offices.some((o) => String(o.id).startsWith("tmp-"))) {
                        // unsaved new office — persist it first so it can be a site
                        const fresh = await saveClinic();
                        if (!fresh) return;
                        offs = fresh.offices || [];
                      }
                      setClinicCtx({
                        code: clinic.code, code_display: clinic.code_display,
                        offices: offs,
                        physician_name: clinic.physician_name || "",
                        cpso: clinic.cpso || "",
                        email: clinic.email || "",
                      });
                      setForm({ ...EMPTY });
                      setCriteria([false, false, false, false, false]);
                      setConsent(false);
                      setCritError(false);
                      setFaxConflict(null);
                      setSiteSel("");
                      setSaveOffice(true);
                      setView("landing");
                    }}
                  >Register a client</button>
                  <button
                    style={{ ...S.btn, marginTop: 0, height: 40, flex: 1 }}
                  >Update clinic info</button>
                </div>
                <Field label="Clinician Name" required><input style={S.inp} value={clinicForm.physician_name} onChange={cf("physician_name")} data-testid="clinic-edit-physician" /></Field>
                <Field label="License Number" required><input style={S.inp} value={clinicForm.cpso} onChange={cf("cpso")} /></Field>
                <Field label="Email" required><input style={S.inp} value={clinicForm.email} onChange={cf("email")} data-testid="clinic-edit-email" /></Field>
                {hasQualified && (
                <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 10, fontSize: 12, color: "#3E4C46", lineHeight: 1.45, cursor: "pointer" }} data-testid="clinic-payall-wrap">
                  <input type="checkbox" checked={payAll} onChange={(e) => setPayAll(e.target.checked)} data-testid="clinic-payall-checkbox" style={{ width: 16, height: 16, marginTop: 1, accentColor: GREEN, flexShrink: 0 }} />
                  <span><b>Use this email for all payments</b>- otherwise payments will be sent to site email.</span>
                </label>
                )}
                <button style={{ ...S.btn, marginTop: 12 }} onClick={saveClinic} disabled={busy} data-testid="clinic-save-btn">
                  {busy ? "Saving…" : "Save clinic information"}
                </button>

                {faxConflictBox}
                <div style={{ ...S.secband, margin: "16px 0 12px" }}>
                  <span>Office locations</span>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".02em", textTransform: "none", color: MUTED }}>{offices.length} saved</span>
                </div>
                {offices.map((o) => {
                  const [l1, l2] = addrLines(o.address);
                  return (
                    <div key={o.id} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", marginBottom: 8 }} data-testid={`office-card-${o.id}`}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{o.name}</span>
                        {officeEdit !== o.id && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => openOfficeEdit(o)} data-testid={`office-edit-${o.id}`}>Edit</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 3, lineHeight: 1.45 }}>{l1}{l2 ? <><br />{l2}</> : null}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>Tel {o.phone_display || fmtPhone(o.phone)} · Fax {o.fax_display || fmtPhone(o.fax)}</div>
                      {!payAll && o.email && (
                        <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }} data-testid={`office-email-${o.id}`}>Payments → {o.email}</div>
                      )}
                      {officeEdit === o.id && officeFormBlock(false)}
                    </div>
                  );
                })}
                {officeEdit === "new" ? officeFormBlock(true) : (
                  <div
                    style={{ border: "1.5px dashed #C9C4B8", borderRadius: 10, padding: 11, textAlign: "center", fontSize: 12.5, fontWeight: 700, color: LABEL, cursor: "pointer", background: "#FAFAF7" }}
                    onClick={openOfficeAdd}
                    data-testid="office-add-btn"
                  >+ Add another office</div>
                )}
                <div style={{ fontSize: 11, color: MUTED, padding: "8px 2px 0", lineHeight: 1.5 }}>
                  Client updates (blood work, results, qualification) are faxed to the office that registered the client.
                </div>

                {clinicClients.length > 0 && (
                  <div style={{ marginTop: 18 }} data-testid="clinic-clients-list">
                    <SecBand right={`${clinicClients.length} total`}>Registered clients</SecBand>
                    <select
                      style={{ ...S.inp, marginBottom: 10 }}
                      value={officeFilter}
                      onChange={(e) => setOfficeFilter(e.target.value)}
                      data-testid="clients-office-filter"
                    >
                      <option value="">Please choose…</option>
                      <option value="__all">{`All offices · ${clinicClients.length}`}</option>
                      {offices.map((o) => (
                        <option key={o.id} value={o.id}>
                          {`${o.name} · ${clinicClients.filter((c) => (c.office_id || offices[0]?.id) === o.id).length}`}
                        </option>
                      ))}
                    </select>
                    {filteredClients.map((c) => (
                      <div key={c.id} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                        padding: "8px 4px", borderBottom: `1px solid #F1F0EC`, fontSize: 12.5,
                      }}>
                        <span style={{ minWidth: 0 }}>
                          <b style={{ display: "block" }}>{c.first_name} {c.last_name}</b>
                          <span style={{ ...S.refcode, color: NAVY, fontSize: 11.5, display: "block", marginTop: 1 }}>{c.code_display}</span>
                          <span style={{ fontSize: 10.5, color: MUTED, display: "block", marginTop: 1 }} data-testid={`client-office-${c.id}`}>{c.office_name || clinic.clinic_name}</span>
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                          background: c.status === "approved" ? "rgba(46,125,91,.12)" : c.status === "denied" ? "rgba(180,53,27,.10)" : "rgba(229,138,46,.14)",
                          color: c.status === "approved" ? SUCCESS : c.status === "denied" ? RED : "#9A6208",
                        }}>{c.status === "approved" ? `Qualified · ${c.total_weeks} wks` : c.status === "denied" ? "Not eligible" : c.status === "pending" ? "Pending" : "Under review"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: 11.5, color: MUTED, textAlign: "center", marginTop: 14 }}>
              <b style={{ cursor: "pointer", color: LABEL }} onClick={() => { setView("landing"); setLookupType(null); setClinicCtx(null); setFaxConflict(null); }} data-testid="back-to-form">← Back to registration</b>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════ LANDING + FORM ══════════════
  return (
    <div style={S.page} data-testid="referrals-landing">
      <div style={S.hero}><div style={S.heroInner}>
        <h2 style={S.heroTitle}>HepCURE Registration</h2>
      </div></div>
      <div style={S.container}>
        <div style={S.card}>
          {/* quick access — returning clinics/clients enter their code right
              away instead of scrolling to the bottom "Check / manage status" link */}
          <div style={{
            border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 11px",
            marginBottom: 14, background: "#FAFAF7",
          }} data-testid="quick-code-box">
            <div style={{ fontSize: 12, color: LABEL, marginBottom: 8, lineHeight: 1.5 }}>
              Enter existing clinic code to register new clients and patient code to report treatment reminders.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ ...S.inp, ...S.refcode, flex: 1, height: 34, fontSize: 12.5, textTransform: "uppercase", marginTop: 0 }}
                placeholder="XXXX-XXXX" value={code}
                data-testid="quick-code-input"
                onChange={(e) => setCode(displayCode(e.target.value))}
                onKeyDown={(e) => e.key === "Enter" && doLookup()}
              />
              <button
                style={{ ...S.btn, width: 72, marginTop: 0, height: 34, fontSize: 12.5, borderRadius: 8 }}
                onClick={() => doLookup()} disabled={busy}
                data-testid="quick-code-find-btn"
              >Find</button>
            </div>
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginBottom: 14, textAlign: "center" }} data-testid="form-intro">
            Complete form below to register first client
          </div>

          {critError && (
            <div style={{
              border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px",
              background: "rgba(180,53,27,.04)", fontSize: 12, fontWeight: 700,
              color: RED, lineHeight: 1.45, marginBottom: 10,
            }} data-testid="criteria-error">
              ⚠ Please select at least 1 criteria to continue.
            </div>
          )}

          <form onSubmit={submitRegistration} noValidate>
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ ...S.secband, border: 0, borderRadius: 0, marginBottom: 0, borderBottom: `1px solid ${BORDER}`, flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                <span>Eligibility criteria</span>
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: "none", letterSpacing: ".02em", color: MUTED, whiteSpace: "nowrap" }}>select all that apply · at least 1 required</span>
              </div>
              {CRITERIA.map((c, i) => (
                <div key={i}
                  data-testid={`criteria-${i}`}
                  onClick={() => toggleCrit(i)}
                  style={{
                    display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 13px",
                    borderBottom: i < CRITERIA.length - 1 ? `1px solid #F1F0EC` : 0,
                    cursor: "pointer", background: criteria[i] ? "rgba(15,59,46,.05)" : "#fff",
                  }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                    border: criteria[i] ? 0 : `1.5px solid ${BORDER}`,
                    background: criteria[i] ? GREEN : "#fff", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 900,
                  }}>{criteria[i] ? "✓" : ""}</div>
                  <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{c}</span>
                </div>
              ))}
            </div>
            <div style={{
              border: `1.5px solid ${RED}`, borderRadius: 12, padding: "12px 14px",
              background: "rgba(180,53,27,.04)", fontSize: 12, fontWeight: 700,
              color: RED, lineHeight: 1.45, margin: "-4px 0 14px",
            }} data-testid="criteria-note">
              ⚠ If client does not meet one of the above eligibility criteria they should not be registered in our program.
            </div>

            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ ...S.secband, border: 0, borderRadius: 0, marginBottom: 0, borderBottom: `1px solid ${BORDER}` }}>Patient information</div>
              <div style={{ padding: "4px 13px 12px" }}>
                <Row>
                  <Field label="Registration Date" half><input style={{ ...S.inp, background: BG }} readOnly value={fmtDateLong(new Date().toISOString())} data-testid="reg-date" /></Field>
                  <Field label="Date of Birth" required half><input type="date" {...inp("dob")} value={form.dob} onChange={set("dob")} required data-testid="reg-dob" /></Field>
                </Row>
                <Row>
                  <Field label="First Name" required half><input {...inp("first_name")} value={form.first_name} onChange={set("first_name")} required placeholder="Jane" data-testid="reg-first-name" /></Field>
                  <Field label="Last Name" required half><input {...inp("last_name")} value={form.last_name} onChange={set("last_name")} required placeholder="Doe" data-testid="reg-last-name" /></Field>
                </Row>
                <Field label="Health Card Number" required>
                  <input {...inp("health_card")} value={form.health_card} onChange={set("health_card", fmtHealthCard)} required placeholder="1234567890 AB" data-testid="reg-health-card" />
                </Field>
              </div>
            </div>

            {faxConflictBox}
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
              <div style={{ ...S.secband, border: 0, borderRadius: 0, marginBottom: 0, borderBottom: `1px solid ${BORDER}`, flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                <span>Clinic information</span>
                {!clinicCtx && (
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: "none", letterSpacing: ".02em", color: TEAL, whiteSpace: "nowrap" }}>Saved to your clinic code</span>
                )}
              </div>
              {clinicCtx ? (
                <div style={{ padding: "4px 13px 12px" }}>
                  <Field label="Registration Site" required>
                    <select {...inp("site")} value={siteSel} onChange={(e) => { setSiteSel(e.target.value); clearMiss("site"); }} required data-testid="reg-site-select">
                      <option value="">Choose registration site</option>
                      {(clinicCtx.offices || []).map((o) => (
                        <option key={o.id} value={o.id}>{`${o.name}${o.address ? ` — ${addrLines(o.address)[0]}` : ""}`}</option>
                      ))}
                      <option value="__other">A different office…</option>
                    </select>
                  </Field>
                  {selSite && (
                    <div style={{
                      background: "rgba(13,110,99,.07)", border: "1px solid rgba(13,110,99,.3)",
                      borderRadius: 9, padding: "8px 12px", fontSize: 11.5, color: TEAL,
                      fontWeight: 600, marginTop: 8, lineHeight: 1.5,
                    }} data-testid="reg-site-faxnote">
                      Client updates (blood work, results, qualification) will be faxed to <b>{selSiteFax}</b>.
                    </div>
                  )}
                  {siteSel === "__other" && (
                    <>
                      <Field label="Clinic Name"><input {...inp("clinic_name")} value={form.clinic_name} onChange={set("clinic_name")} placeholder="East End Walk-in" data-testid="reg-clinic-name" /></Field>
                      <Field label="Address" required><input {...inp("street")} value={form.street} onChange={set("street")} required placeholder="2100 Danforth Ave" data-testid="reg-street" /></Field>
                      <Row>
                        <Field label="Province" required half><input style={{ ...S.inp, background: BG }} value={form.province} readOnly required data-testid="reg-province" /></Field>
                        <Field label="Postal Code" required half><input {...inp("postal")} value={form.postal} onChange={set("postal")} required placeholder="M4C 1K1" data-testid="reg-postal" /></Field>
                      </Row>
                      <Row>
                        <Field label="Telephone" required half><input {...inp("phone")} inputMode="tel" value={form.phone} onChange={set("phone")} required placeholder="(416) 555-0166" data-testid="reg-phone" /></Field>
                        <Field label="Fax" required half><input {...inp("fax")} inputMode="tel" value={form.fax} onChange={set("fax")} required placeholder="(416) 555-0171" data-testid="reg-fax" /></Field>
                      </Row>
                      <div style={{ fontSize: 11, color: MUTED, padding: "6px 0 0 4px" }}>
                        Client updates (blood work, results, qualification) are faxed to this number automatically.
                      </div>
                      <Field label="Email" required><input type="email" {...inp("email")} value={form.email} onChange={set("email")} required placeholder="billing@eastendwalkin.ca" data-testid="reg-email" /></Field>
                      <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginTop: 10, fontSize: 12, color: "#3E4C46", lineHeight: 1.45, cursor: "pointer" }} data-testid="reg-save-office-wrap">
                        <input type="checkbox" checked={saveOffice} onChange={(e) => setSaveOffice(e.target.checked)} data-testid="reg-save-office" style={{ width: 16, height: 16, marginTop: 1, accentColor: GREEN, flexShrink: 0 }} />
                        <span><b>Save this office to my account.</b><br />It will appear in the site list next time.</span>
                      </label>
                    </>
                  )}
                </div>
              ) : (
              <div style={{ padding: "4px 13px 12px" }}>
                <Field label="Clinician Name" required><input {...inp("physician_name")} value={form.physician_name} onChange={set("physician_name")} required placeholder="Anil Sharma" data-testid="reg-physician" /></Field>
                <Field label="Clinic Name"><input {...inp("clinic_name")} value={form.clinic_name} onChange={set("clinic_name")} placeholder="Riverside Community Health Centre" data-testid="reg-clinic-name" /></Field>
                <Field label="License Number" required><input {...inp("cpso")} value={form.cpso} onChange={set("cpso")} required placeholder="123456" data-testid="reg-cpso" /></Field>
                <Field label="Address" required><input {...inp("street")} value={form.street} onChange={set("street")} required placeholder="123 Main St" data-testid="reg-street" /></Field>
                <Row>
                  <Field label="Province" required half><input style={{ ...S.inp, background: BG }} value={form.province} readOnly required data-testid="reg-province" /></Field>
                  <Field label="Postal Code" required half><input {...inp("postal")} value={form.postal} onChange={set("postal")} required placeholder="M5V 1J1" data-testid="reg-postal" /></Field>
                </Row>
                <Row>
                  <Field label="Telephone" required half><input {...inp("phone")} inputMode="tel" value={form.phone} onChange={set("phone")} required placeholder="(416) 555-0143" data-testid="reg-phone" /></Field>
                  <Field label="Fax" required half><input {...inp("fax")} inputMode="tel" value={form.fax} onChange={set("fax")} required placeholder="(416) 555-0192" data-testid="reg-fax" /></Field>
                </Row>
                <div style={{ fontSize: 11, color: MUTED, padding: "6px 0 0 4px" }}>
                  Client updates (blood work, results, qualification) are faxed to this number automatically.
                </div>
                <Field label="Email" required><input type="email" {...inp("email")} value={form.email} onChange={set("email")} required placeholder="billing@riversideclinic.ca" data-testid="reg-email" /></Field>
              </div>
              )}
            </div>

            <div style={{
              background: "#FFFBEA", border: "1px solid #F0E4B8", borderRadius: 10,
              padding: "10px 13px", fontSize: 11.5, color: "#7A6410", lineHeight: 1.5,
            }} data-testid="consent-disclaimer">
              <b>⚠ Please read before submitting</b>
              <div style={{ marginTop: 4 }}>
                By submitting this form, you confirm the client has provided <b>verbal consent</b> to be registered with HepCURE and to be contacted about their participation in our Hepatitis C screening and treatment program.
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, cursor: "pointer", fontWeight: 700, color: "#5E4400" }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="consent-checkbox" style={{ width: 16, height: 16 }} />
                Verbal consent confirmed by clinic
              </label>
            </div>

            <button type="submit" style={S.btn} disabled={busy} data-testid="register-submit-btn">
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </form>
          <div style={{ fontSize: 12, color: MUTED, textAlign: "center", marginTop: 12 }}>
            Already submitted? <b style={{ cursor: "pointer", color: LABEL }} onClick={() => navView("lookup")} data-testid="goto-lookup">Check / manage status →</b>
          </div>
        </div>
      </div>
    </div>
  );
}
